import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { scrollState, onPhaseChange } from "../state/scroll";
import { useContent } from "../state/siteContent";
import ThemeToggle from "./ThemeToggle";

// Phases that should reveal the navbar. INTRO and MAP keep the experience
// fullscreen; PLOT (and anything that comes after it, e.g. construction /
// reservation sections) gets the persistent top nav.
const VISIBLE_PHASES = new Set(["PLOT"]);

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

export default function HomeNavbar() {
  const [phase, setPhase] = useState(scrollState.phase);
  const [open, setOpen] = useState(false);
  const { settings } = useContent();

  useEffect(() => onPhaseChange(setPhase), []);

  const visible = VISIBLE_PHASES.has(phase);

  useEffect(() => {
    if (!visible) setOpen(false);
  }, [visible]);

  // Lock body scroll when the mobile drawer is open. We don't touch overflow
  // otherwise — Lenis owns scroll on the homepage. Setting body overflow
  // alone doesn't work here: Lenis intercepts wheel/touch events itself and
  // keeps smooth-scrolling the page underneath the open drawer regardless
  // of the native overflow value, so Lenis itself must also be paused
  // (same window.__lenis stop/start handle SmoothScroll.jsx uses for the
  // INTRO lock and MAP phase).
  useEffect(() => {
    if (!open) return undefined;
    const lenis = window.__lenis;
    try { lenis?.stop(); } catch (_) {}
    document.body.style.overflow = "hidden";
    return () => {
      try { lenis?.start(); } catch (_) {}
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`home-nav ${visible ? "is-visible" : ""}`}
        aria-hidden={!visible}
      >
        <Link to="/" className="home-nav__brand" aria-label={`${settings.brandName} - ${settings.brandTagline}`}>
          <span className="brand-chip home-nav__brand-chip">
            <img
              src={settings.logoMark}
              alt={settings.brandName}
              className="home-nav__brand-logo"
              width="56"
              height="44"
              decoding="async"
            />
          </span>
        </Link>

        <nav className="home-nav__links" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `home-nav__link ${isActive ? "is-active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="home-nav__right">
          <ThemeToggle />
          <Link to="/contact" className="home-nav__cta">Reserve</Link>
        </div>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className={`home-nav__menu-btn ${open ? "is-open" : ""}`}
          onClick={() => setOpen((v) => !v)}
        >
          <span />
        </button>
      </header>

      <div
        className={`home-nav__mobile ${open && visible ? "is-open" : ""}`}
        aria-hidden={!(open && visible)}
      >
        {LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={() => setOpen(false)}
            className={({ isActive }) => (isActive ? "is-active" : "")}
          >
            {l.label}
          </NavLink>
        ))}
        <Link to="/contact" onClick={() => setOpen(false)} className="home-nav__mobile-cta">
          Reserve a plot
        </Link>
        <div className="home-nav__mobile-toggle">
          <ThemeToggle />
        </div>
      </div>

      <style>{`
        .home-nav {
          position: fixed;
          top: 18px;
          left: 50%;
          transform: translate(-50%, -12px);
          width: min(70%, 1100px);
          z-index: 60;
          display: flex;
          align-items: center;
          gap: clamp(16px, 2vw, 28px);
          padding: 12px clamp(18px, 2vw, 24px);
          background: var(--nav-pill-bg);
          backdrop-filter: blur(14px) saturate(140%);
          -webkit-backdrop-filter: blur(14px) saturate(140%);
          border: 1px solid var(--nav-border);
          border-radius: 999px;
          opacity: 0;
          pointer-events: none;
          transition: opacity 700ms ease, transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1);
        }
        .home-nav.is-visible {
          opacity: 1;
          transform: translate(-50%, 0);
          pointer-events: auto;
        }

        .home-nav__brand {
          display: inline-flex;
          align-items: center;
          line-height: 1;
          text-decoration: none;
          color: var(--color-cream);
          padding-right: clamp(8px, 1.4vw, 16px);
          border-right: 1px solid var(--line-2);
        }
        .home-nav__brand-chip {
          padding: 3px 7px;
          border-radius: 6px;
        }
        .home-nav__brand-logo {
          height: clamp(30px, 3.4vw, 40px);
          width: auto;
          display: block;
        }
        .home-nav__right {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-left: 12px;
        }

        .home-nav__links {
          display: flex;
          align-items: center;
          gap: clamp(14px, 2vw, 26px);
          margin-left: auto;
        }

        .home-nav__link {
          position: relative;
          font-family: var(--font-ui);
          font-size: 11px;
          letter-spacing: var(--tracking-wider);
          text-transform: uppercase;
          color: var(--color-cream-dim);
          text-decoration: none;
          padding: 4px 0;
          transition: color 300ms ease;
        }
        .home-nav__link::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: -2px;
          width: 0;
          height: 1px;
          background: var(--color-gold);
          transition: width 350ms ease, left 350ms ease;
        }
        .home-nav__link:hover,
        .home-nav__link.is-active {
          color: var(--color-cream);
        }
        .home-nav__link:hover::after,
        .home-nav__link.is-active::after {
          width: 100%;
          left: 0;
        }

        .home-nav__cta {
          font-family: var(--font-ui);
          font-size: 10px;
          letter-spacing: var(--tracking-wider);
          text-transform: uppercase;
          padding: 8px 18px;
          border: 1px solid var(--color-gold-dim);
          color: var(--color-gold);
          background: transparent;
          text-decoration: none;
          border-radius: 999px;
          transition: background 300ms ease, color 300ms ease, border-color 300ms ease;
        }
        .home-nav__cta:hover {
          background: var(--color-gold);
          color: var(--btn-ink);
          border-color: var(--color-gold);
        }

        .home-nav__menu-btn {
          display: none;
          width: 44px;
          height: 44px;
          background: transparent;
          border: 1px solid var(--nav-border);
          color: var(--color-cream);
          cursor: pointer;
          border-radius: 999px;
          align-items: center;
          justify-content: center;
        }
        .home-nav__menu-btn span {
          display: block;
          width: 16px;
          height: 1px;
          background: var(--color-cream);
          position: relative;
        }
        .home-nav__menu-btn span::before,
        .home-nav__menu-btn span::after {
          content: "";
          position: absolute;
          left: 0;
          width: 16px;
          height: 1px;
          background: var(--color-cream);
          transition: transform 300ms ease, top 300ms ease;
        }
        .home-nav__menu-btn span::before { top: -5px; }
        .home-nav__menu-btn span::after  { top: 5px; }

        .home-nav__menu-btn.is-open span { background: transparent; }
        .home-nav__menu-btn.is-open span::before { top: 0; transform: rotate(45deg); }
        .home-nav__menu-btn.is-open span::after  { top: 0; transform: rotate(-45deg); }

        .home-nav__mobile {
          position: fixed;
          inset: 76px 12px auto 12px;
          background: var(--nav-mobile-bg);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          border: 1px solid var(--nav-border);
          border-radius: 24px;
          padding: 28px 0 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 22px;
          opacity: 0;
          transform: translateY(-8px);
          pointer-events: none;
          transition: opacity 350ms ease, transform 350ms ease;
          z-index: 59;
        }
        .home-nav__mobile.is-open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
        .home-nav__mobile a {
          font-family: var(--font-display);
          font-weight: 300;
          font-size: 26px;
          letter-spacing: var(--tracking-tight);
          color: var(--color-cream);
          text-decoration: none;
        }
        .home-nav__mobile a.is-active { color: var(--color-gold); }
        .home-nav__mobile-cta {
          margin-top: 6px;
          font-family: var(--font-ui) !important;
          font-size: 11px !important;
          letter-spacing: var(--tracking-wider);
          text-transform: uppercase;
          padding: 12px 24px;
          border: 1px solid var(--color-gold-dim);
          color: var(--color-gold) !important;
          border-radius: 999px;
        }
        .home-nav__mobile-toggle {
          margin-top: 8px;
          transform: scale(1.15);
        }


        [data-theme="light"] .home-nav {
          background: rgba(255, 250, 240, 0.78);
          border-color: rgba(20, 35, 26, 0.12);
          box-shadow: 0 18px 48px rgba(30, 45, 30, 0.12);
        }
        [data-theme="light"] .home-nav__brand {
          border-right-color: rgba(20, 35, 26, 0.12);
        }
        [data-theme="light"] .home-nav__cta {
          background: rgba(15, 92, 51, 0.07);
          border-color: rgba(15, 92, 51, 0.2);
        }
        [data-theme="light"] .home-nav__mobile {
          background: rgba(255, 250, 240, 0.96);
          border-color: rgba(20, 35, 26, 0.12);
          box-shadow: 0 18px 48px rgba(30, 45, 30, 0.12);
        }
        @media (max-width: 860px) {
          .home-nav {
            width: calc(100% - 24px);
            top: 14px;
            padding: 10px 14px;
          }
          .home-nav__links { display: none; }
          .home-nav__right { display: none; }
          .home-nav__menu-btn { display: inline-flex; margin-left: auto; }
        }

        @media (prefers-reduced-motion: reduce) {
          .home-nav { transition: opacity 200ms linear; transform: translate(-50%, 0); }
          .home-nav__mobile { transition: opacity 150ms linear; transform: none; }
        }
      `}</style>
    </>
  );
}

